import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';

import {
  fetchMarketingExpertRatings,
  toExpertRatingsPreviewRow,
  toHeroTeaserRow,
  type MarketingExpertRatingApiRow,
} from './marketingExpertRatings';
import { HERO_TEASER_ROW_COUNT } from './HeroRatingsTeaserLite';
import type { ExpertRatingsPreviewRow } from './fixtures/marketingPreviewData';
import type { HeroLeaderboardPreviewRow } from './fixtures/marketingPreviewData';

const DEFAULT_APP_URL = 'https://app.wagerwatchdog.com';
const DEFAULT_API_BASE = 'https://api.wagerwatchdog.com';

type MarketingRatingsContextValue = {
  rows: MarketingExpertRatingApiRow[];
  previewRows: Array<ExpertRatingsPreviewRow & { profile_url: string }>;
  heroRows: Array<HeroLeaderboardPreviewRow & { profile_url: string }>;
  isLoading: boolean;
  error: string | null;
  updatedAt: string | null;
};

const MarketingRatingsContext = createContext<MarketingRatingsContextValue>({
  rows: [],
  previewRows: [],
  heroRows: [],
  isLoading: true,
  error: null,
  updatedAt: null,
});

type MarketingRatingsProviderProps = {
  children: ReactNode;
  apiBaseUrl?: string;
  appBaseUrl?: string;
};

export function MarketingRatingsProvider({
  children,
  apiBaseUrl = DEFAULT_API_BASE,
  appBaseUrl = DEFAULT_APP_URL,
}: MarketingRatingsProviderProps) {
  const [rows, setRows] = useState<MarketingExpertRatingApiRow[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [updatedAt, setUpdatedAt] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setIsLoading(true);
    setError(null);
    fetchMarketingExpertRatings(apiBaseUrl)
      .then((payload) => {
        if (cancelled) return;
        setRows(payload.data ?? []);
        setUpdatedAt(payload.updated_at ?? null);
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setRows([]);
        setUpdatedAt(null);
        setError(
          err instanceof Error ? err.message : 'Failed to load expert ratings',
        );
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [apiBaseUrl]);

  const previewRows = useMemo(
    () => rows.map((row) => toExpertRatingsPreviewRow(row, appBaseUrl)),
    [rows, appBaseUrl],
  );
  const heroRows = useMemo(
    () =>
      rows
        .slice(0, HERO_TEASER_ROW_COUNT)
        .map((row) => toHeroTeaserRow(row, appBaseUrl)),
    [rows, appBaseUrl],
  );

  const value = useMemo(
    () => ({
      rows,
      previewRows,
      heroRows,
      isLoading,
      error,
      updatedAt,
    }),
    [rows, previewRows, heroRows, isLoading, error, updatedAt],
  );

  return (
    <MarketingRatingsContext.Provider value={value}>
      {children}
    </MarketingRatingsContext.Provider>
  );
}

export function useMarketingRatings(): MarketingRatingsContextValue {
  return useContext(MarketingRatingsContext);
}
